import {loginPath} from "../lib/clinicSignIn";
import React, {useState, useEffect, useRef} from "react";
import {checkResetParams, resetPassword} from "../authRequests";
import {getResetTokenFromHref} from "../utils/resetToken";
import {useParams, useNavigate} from "react-router-dom";
import {loadDataForPatientLogin, crud, viewConsentForms} from "../crudRequests";
import clientInfo from "../additional_files/loginInfo";
import {HiShieldCheck} from "react-icons/hi";
import {RiDeleteBin5Line} from "react-icons/ri";
import {MdClose} from "react-icons/md";
import {
  FiFileText,
  FiCheckCircle,
  FiCircle,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import {
  Box,
  Flex,
  VStack,
  Text,
  Button,
  Image,
  Spinner,
  IconButton,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Heading,
  List,
  ListItem,
  Icon,
  useColorModeValue,
  HStack,
  Badge,
  Divider,
  Center,
} from "@chakra-ui/react";

function getResetErrorPayload(error) {
  const responseData = error?.response?.data;
  if (responseData && typeof responseData === "object") {
    return {
      title: responseData.title || "Reset link no longer available",
      message:
        responseData.message ||
        "This password reset link is no longer valid. Please request a new one to continue.",
    };
  }

  if (error?.response?.status === 410) {
    return {
      title: "Reset link no longer available",
      message:
        "This password reset link has already been used or is no longer valid. Please request a new reset link to continue.",
    };
  }

  return {
    title: "Invalid reset link",
    message:
      "This password reset link is invalid. Please request a new reset link and try again.",
  };
}

// `db`: the clinic the link names (/login/<db>?reset=…), whose sign-in page a used link sends the patient back to.
export default function ResetPwdForm({db} = {}) {
  const [pid, setPid] = useState("");
  const [rid, setRid] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [userType, setUserType] = useState("");
  const [consentForms, setConsentForms] = useState([]);

  const [serverError, setServerError] = useState("");
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isCheckingReset, setIsCheckingReset] = useState(true);
  const [invalidResetInfo, setInvalidResetInfo] = useState(null);
  // The clinic's terms could not be loaded (not a dead link): said by the button until it is pressed again.
  const [termsUnavailable, setTermsUnavailable] = useState(false);

  const loadedRef = useRef(null);
  const loaderRef = useRef(null);
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signature, setSignature] = useState(false);
  const [resetType, setResetType] = useState("");

  useEffect(() => {
    const checkPasswords = async () => {
      if (password === confirmPassword) {
        setPasswordsMatch(true);
      } else {
        setPasswordsMatch(false);
      }
    };

    checkPasswords();
  }, [password, confirmPassword]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  function reset(e) {
    if (org?.db) {
      e.preventDefault();
      if (passwordsMatch && password.length >= 8) {
        setLoading(true);

        const action = () => {
          let reset = getResetTokenFromHref();
          setLoading(true);
          resetPassword(
            reset,
            userType === "patient" ? pid : rid,
            password,
            userType,
            org.db,
            resetType
          )
            .then(async (res) => {
              let {user} = res.data||{};
              const relatedPatient = res.data?.patient || null;
              let {jwt, userType, db, tfa} = user||{};
              loaderRef.current.className = "block";
              loadedRef.current.className = "hidden";

              loadDataForPatientLogin(
                {
                  jwt: jwt,
                  patient: relatedPatient || user,
                  relationship: userType==="relationship"?user:null,
                  destination: "../",
                  db: db,
                  userType,
                },
                navigate
              );

              let jsonIp = await axios({
                url: "https://api.ipify.org?format=json",
              });
              let userId = userType === "relationship" ? user.rid : user.id;

              crud({jwt: jwt}, [
                {
                  db: db,
                  collection: "security",
                  parameters: [
                    {userId},
                    {
                      $push: {
                        logins: {
                          ip: jsonIp.data.ip,
                          client: clientInfo(),
                          timestamp: new Date(),
                        },
                      },
                    },
                    {upsert: true},
                  ],
                  method: "findOneAndUpdate",
                },
              ]);
            })
            .catch((err) => {
              console.log(err);
              setInvalidResetInfo(getResetErrorPayload(err));
            })
            .then(() => {
              setLoading(false);
            });
        };

        if (signature) {
          action();
        } else {
          setTermsUnavailable(false);
          viewConsentForms(getResetTokenFromHref())
            .then((res) => {
              if ((res?.data || []).length > 0) {
                setConsentForms(res?.data || []);
                setLoading(false);
              } else action();
            })
            .catch((err) => {
              // The terms could not be read, so no password is set without them. A link that stopped working while the
              // page was open is answered as a dead link; anything else leaves the form to be sent again.
              if (err?.response?.status === 400 && err.response.data?.code === "RESET_LINK_INVALID") {
                setInvalidResetInfo(getResetErrorPayload(err));
              } else setTermsUnavailable(true);
              setLoading(false);
            });
        }
      }
    }
  }

  useEffect(
    function () {
      let reset = getResetTokenFromHref();
      checkResetParams(reset)
        .then((response) => {
          setEmail(response.data.email);
          setPid(response.data.pid);
          setRid(response.data.rid);
          setOrg(response.data.org);
          setSignature(response.data.consentSignature);
          setUserType(response.data.userType);
          setResetType(response.data.resetType);
        })
        .catch((err) => {
          console.log(err);
          setInvalidResetInfo(getResetErrorPayload(err));
        })
        .finally(() => {
          setIsCheckingReset(false);
        });
    },

    []
  );

  if (isCheckingReset) {
    return (
      <div className="h-full min-h-screen grid place-items-center px-10 py-10 rounded-lg">
        <div className="border p-[6.5rem] rounded-md bg-white">
          <div className="flex justify-center align-middle">
            <img src="/images/loader.gif" className="h-36" alt="Loading" />
          </div>
        </div>
      </div>
    );
  }

  if (invalidResetInfo) {
    return (
      <div className="h-full min-h-screen grid place-items-center px-6 py-10 rounded-lg">
        <div className="w-full max-w-md border border-gray-100 bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-8 sm:p-10 text-center">
          {org?.logo ? (
            <img
              src={org.logo}
              className="w-40 mx-auto mb-6"
              alt={`${org?.name || "Clinic"} logo`}
            />
          ) : (
            <img
              src="/images/logo.png"
              className="w-40 mx-auto mb-6"
              alt="Brightlight logo"
            />
          )}
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <MdClose className="h-6 w-6" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">
            {invalidResetInfo.title}
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            {invalidResetInfo.message}
          </p>
          <button
            type="button"
            onClick={() => {
              window.location.href = loginPath(db || undefined);
            }}
            className="w-full p-2.5 px-5 rounded-lg bg-[#6772e5] hover:bg-[#5b63d3] text-white font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6772e5] transition-all duration-150"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }
  
  return !(consentForms?.length > 0) ? (
    <div className="h-full grid place-items-center px-10 py-10 rounded-lg">
      <div ref={loaderRef} className="hidden">
        <div className="border p-[6.5rem] rounded-md">
          <div className="flex justify-center align-middle">
            <img src="/images/loader.gif" className="h-36" alt="Loading" />
          </div>
        </div>
      </div>

      <div ref={loadedRef} className="w-full max-w-md">
        <form
          onSubmit={reset}
          className="w-full border border-gray-100 bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl p-8 sm:p-10"
        >
          {org?.logo ? (
            <img
              src={org.logo}
              className="w-40 mx-auto mb-6"
              alt={`${org?.name || "Clinic"} logo`}
            />
          ) : (
            <img
              src="/images/logo.png"
              className="w-40 mx-auto mb-6"
              alt="Brightlight logo"
            />
          )}
          {authError && (
            <div className="">
              <div className="mb-5 flex w-full max-w-sm overflow-hidden bg-white rounded-lg shadow-md dark:bg-gray-800">
                <div className="flex items-center justify-center w-12 bg-red-500">
                  <svg
                    className="w-6 h-6 text-white fill-current"
                    viewBox="0 0 40 40"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M20 3.36667C10.8167 3.36667 3.3667 10.8167 3.3667 20C3.3667 29.1833 10.8167 36.6333 20 36.6333C29.1834 36.6333 36.6334 29.1833 36.6334 20C36.6334 10.8167 29.1834 3.36667 20 3.36667ZM19.1334 33.3333V22.9H13.3334L21.6667 6.66667V17.1H27.25L19.1334 33.3333Z" />
                  </svg>
                </div>

                <div className="px-4 py-2 -mx-3">
                  <div className="mx-3">
                    <span className="font-semibold text-red-500 dark:text-red-400">
                      Error
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-200">
                      Invalid link!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {serverError && (
            <div className="">
              <div className="mb-5 flex w-full max-w-sm overflow-hidden bg-white rounded-lg shadow-md dark:bg-gray-800">
                <div className="flex items-center justify-center w-12 bg-red-500">
                  <svg
                    className="w-6 h-6 text-white fill-current"
                    viewBox="0 0 40 40"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M20 3.36667C10.8167 3.36667 3.3667 10.8167 3.3667 20C3.3667 29.1833 10.8167 36.6333 20 36.6333C29.1834 36.6333 36.6334 29.1833 36.6334 20C36.6334 10.8167 29.1834 3.36667 20 3.36667ZM19.1334 33.3333V22.9H13.3334L21.6667 6.66667V17.1H27.25L19.1334 33.3333Z" />
                  </svg>
                </div>

                <div className="px-4 py-2 -mx-3">
                  <div className="mx-3">
                    <span className="font-semibold text-red-500 dark:text-red-400">
                      Error
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-200">
                      <span className="font-semibold">
                        The server has encountered an
                        <br />
                        internal error.
                      </span>
                      <br />
                      Please try again later.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mb-4">
            <h2 className="text-xl font-semibold text-slate-800">
              Create a new password
            </h2>
            <p className="text-sm text-slate-500">Use at least 8 characters</p>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Please choose a new password (min. 8 letters) for your Brightlight
            Account associated with <b>{email}</b>.
          </p>
          <PasswordField
            id="create_password"
            label="Create Password"
            value={password}
            onChange={handlePasswordChange}
            showPassword={showPassword}
            onToggle={() => setShowPassword((prev) => !prev)}
          />
          <PasswordField
            id="confirm_password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            showPassword={showConfirmPassword}
            onToggle={() => setShowConfirmPassword((prev) => !prev)}
          />
          {password && confirmPassword ? (
            passwordsMatch ? (
              <div className="text-green-500 text-xs mb-1">Passwords match</div>
            ) : (
              <div className="text-red-500 text-xs mb-2">
                Passwords do not match
              </div>
            )
          ) : (
            <span></span>
          )}
          {password ? (
            password.length >= 8 ? (
              <div className="text-green-500 text-xs mb-3">
                Password is satisfactory character length
              </div>
            ) : (
              <div className="text-red-500 text-xs mb-3">
                Password must be min. 8 letters
              </div>
            )
          ) : (
            <span></span>
          )}

          {termsUnavailable && (
            <div className="text-red-500 text-xs mb-3" role="alert">
              The clinic's terms couldn't be loaded. Try again.
            </div>
          )}
          <div className="justify-center">
            <button
              type="submit"
              className="w-full p-2.5 px-5 rounded-lg my-2 flex justify-center items-center bg-[#6772e5] hover:bg-[#5b63d3] text-white font-medium text-sm cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6772e5] transition-all duration-150"
              disabled={loading}
            >
              Continue
              {loading && (
                <span className="">
                  <svg
                    className="animate-spin ml-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : (
    <ConsentForm
      {...{org, userType, rid, pid, password, consentForms, setConsentForms, resetType}}
    />
  );
}

function PasswordField({id, label, value, onChange, showPassword, onToggle}) {
  return (
    <div className="input-group relative">
      <input
        type={showPassword ? "text" : "password"}
        id={id}
        value={value}
        onChange={onChange}
        required
        autoComplete="new-password"
        className="input w-full py-3 pl-4 pr-12 rounded-lg my-2 bg-white border border-gray-200 focus:border-[#6772e5] focus:ring-2 focus:ring-[#6772e5]/40 transition"
        style={{
          fontFamily: showPassword ? "inherit" : "password",
          letterSpacing: showPassword ? "normal" : "2px",
        }}
      />
      <label htmlFor={id} className="input-label">
        {label}
      </label>
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
        aria-label={
          showPassword
            ? `Hide ${label.toLowerCase()}`
            : `Show ${label.toLowerCase()}`
        }
      >
        {showPassword ? <FiEyeOff /> : <FiEye />}
      </button>
    </div>
  );
}

function ConsentForm({
  org,
  userType,
  rid,
  pid,
  password,
  consentForms,
  setConsentForms,
  resetType,
}) {
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const [showPdf, setShowPdf] = useState(null);
  const [imageData, setImageData] = useState(null);
  const sigRef = useRef(null);

  const saveSignature = (signature) => {
    setImageData(signature);
  };

  useEffect(() => {
    const handleEscapeKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowPdf(null);
      }
    };
    window.addEventListener("keydown", handleEscapeKeyDown);
    return () => {
      window.removeEventListener("keydown", handleEscapeKeyDown);
    };
  }, []);

  function disabled() {
    // El botón está habilitado cuando:
    // 1. org.db existe
    // 2. Todos los formularios están confirmados (no hay ninguno sin confirmar)
    // 3. Hay una firma (imageData)
    const allConfirmed = !consentForms.find((f) => !f.confirmed);
    const hasSignature = !!imageData;
    const hasOrg = !!org?.db;
    const isEnabled = hasOrg && allConfirmed && hasSignature;
    
    // Debug: descomentar para ver el estado
    // console.log('Button state:', { hasOrg, allConfirmed, hasSignature, isEnabled, consentForms, imageData: !!imageData });
    
    return isEnabled;
  }

  function reset(e) {
    e.preventDefault();
    if (org?.db && !consentForms.find((f) => !f.confirmed)) {
      setLoader(true);
      let reset = getResetTokenFromHref();
      resetPassword(
        reset,
        userType === "patient" ? pid : rid,
        password,
        userType,
        org.db,
        resetType
      )
        .then(async (res) => {
          let {user} = res.data||{};
          const relatedPatient = res.data?.patient || null;
          let {jwt, userType, db, tfa} = user||{};
          loadDataForPatientLogin(
            {
              jwt: jwt,
              patient: relatedPatient || user,
              relationship: userType==="relationship"?user:null,
              destination: "../",
              db: db,
              userType,
            },
            navigate
          );

          let jsonIp = await axios({url: "https://api.ipify.org?format=json"});
          let userId = userType === "relationship" ? user.rid : user.id;

          crud({jwt: jwt}, [
            {
              db: db,
              collection: "security",
              parameters: [
                {userId},
                {
                  $push: {
                    logins: {
                      ip: jsonIp.data.ip,
                      client: clientInfo(),
                      timestamp: new Date(),
                    },
                  },
                },
                {upsert: true},
              ],
              method: "findOneAndUpdate",
            },
            {
              db: db,
              collection: "patients",
              parameters: [
                {pid: user.id},
                {$set: {consentSignature: imageData}},
              ],
              method: "findOneAndUpdate",
            },
          ]);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  const bgGradient = useColorModeValue(
    "linear(to-br, indigo.50, white, blue.50)",
    "gray.800"
  );
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const listBg = useColorModeValue("white", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.600");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.700", "gray.200");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const containerBg = useColorModeValue("gray.50", "gray.800");
  const successBg = useColorModeValue("green.50", "green.900");
  const successBorder = useColorModeValue("green.200", "green.700");
  const maxW = useBreakpointValue({base: "full", md: "3xl", lg: "4xl"});
  const padding = useBreakpointValue({base: 6, md: 8, lg: 10});

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
      py={10}
    >
      {showPdf && (
        <PDFModal
          form={showPdf}
          setConsentForms={setConsentForms}
          showModal={setShowPdf}
        />
      )}
      {loader ? (
        <Box
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          p={20}
          bg={cardBg}
        >
          <Flex justify="center" align="center">
            <Spinner size="xl" thickness="4px" color="blue.500" />
          </Flex>
        </Box>
      ) : (
        <Box w="full" maxW={maxW}>
          <Box
            as="form"
            onSubmit={reset}
            w="full"
            borderWidth="1px"
            borderColor={borderColor}
            bg={cardBg}
            backdropFilter="blur(10px)"
            boxShadow="xl"
            borderRadius="2xl"
            p={padding}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            {org?.logo ? (
              <Image
                src={org.logo}
                alt={`${org?.name || "Clinic"} logo`}
                maxW="160px"
                mx="auto"
                mb={6}
              />
            ) : (
              <Image
                src="/images/logo.png"
                alt="Brightlight logo"
                maxW="160px"
                mx="auto"
                mb={6}
              />
            )}

            <VStack spacing={6} w="full" align="stretch">
              <Box textAlign="center">
                <Heading size="md" color={headingColor} mb={2}>
                  Consent Forms
                </Heading>
                <Text fontSize="sm" color={subTextColor}>
                  Please review and accept all consent forms to proceed
                </Text>
              </Box>
              
              <Box
                w="full"
                p={4}
                bg={containerBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <VStack spacing={3} w="full" align="stretch">
                  {consentForms.map((f, i) => (
                    <CompactConsentFormItem
                      key={i}
                      form={f}
                      orgName={org.name}
                      isConfirmed={f.confirmed}
                      onView={() => setShowPdf(f)}
                      onToggle={() => {
                        setConsentForms((prev) =>
                          prev.map((e) =>
                            e.fid === f.fid ? {...e, confirmed: !e.confirmed} : e
                          )
                        );
                      }}
                      isLast={i === consentForms.length - 1}
                    />
                  ))}
                </VStack>
              </Box>
              
              {consentForms.every(f => f.confirmed) && (
                <Box
                  p={3}
                  bg={successBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={successBorder}
                >
                  <HStack spacing={2} justify="center">
                    <Icon as={FiCheckCircle} color="green.500" />
                    <Text fontSize="sm" fontWeight="medium" color="green.700">
                      All consent forms have been accepted
                    </Text>
                  </HStack>
                </Box>
              )}
            </VStack>
            
            {consentForms.every(f => f.confirmed) && (
              <>
                <Divider my={6} />
                
                <VStack spacing={4} w="full" align="stretch">
                  <Heading size="md" color={headingColor}>
                    Digital Signature
                  </Heading>
                  <Text fontSize="sm" color={subTextColor}>
                    Please provide your signature to complete the process
                  </Text>
                  <Signature sigRef={sigRef} saveSignature={saveSignature} />
                </VStack>
              </>
            )}
            
            <button
              type="submit"
              className="w-full p-2.5 px-5 rounded-lg my-2 flex justify-center items-center bg-[#6772e5] hover:bg-[#5b63d3] text-white font-medium text-sm cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6772e5] transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:shadow-lg disabled:hover:shadow-indigo-500/20"
              disabled={!disabled() || loader}
            >
              Continue to account
              {loader && (
                <span className="">
                  <svg
                    className="animate-spin ml-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </span>
              )}
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function CompactConsentFormItem({form, orgName, isConfirmed, onView, onToggle, isLast}) {
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const bg = useColorModeValue("white", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.600");
  
  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        p={3}
        borderRadius="lg"
        bg={bg}
        _hover={{bg: hoverBg}}
        transition="all 0.2s"
        gap={3}
      >
        <HStack spacing={3} flex="1" minW={0}>
          <Icon
            as={isConfirmed ? FiCheckCircle : FiCircle}
            fontSize="lg"
            color={isConfirmed ? "green.500" : "gray.400"}
            flexShrink={0}
          />
          <VStack align="start" spacing={0} flex="1" minW={0}>
            <HStack spacing={2} w="full">
              <Icon as={FiFileText} color="indigo.500" fontSize="sm" />
              <Text
                fontSize="xs"
                fontWeight="medium"
                color={textColor}
                noOfLines={1}
              >
                {`${orgName} ${form.formTitle?.replace(/(\.\w+)$/, "")}`}
              </Text>
            </HStack>
          </VStack>
        </HStack>
        <HStack spacing={2} flexShrink={0}>
          <IconButton
            aria-label="View form"
            icon={<FiFileText />}
            size="sm"
            variant="ghost"
            colorScheme="indigo"
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
          />
          <Button
            size="sm"
            colorScheme={isConfirmed ? "green" : "indigo"}
            variant={isConfirmed ? "solid" : "outline"}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            leftIcon={<Icon as={isConfirmed ? HiShieldCheck : FiCircle} fontSize="xs" />}
            fontSize="xs"
            px={3}
            minW="auto"
          >
            {isConfirmed ? "Accepted" : "Accept"}
          </Button>
        </HStack>
      </Flex>
      {!isLast && <Divider />}
    </>
  );
}

function Signature({sigRef, saveSignature}) {
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const bg = useColorModeValue("white", "gray.700");
  const hoverBorder = useColorModeValue("indigo.400", "indigo.500");

  return (
    <Box
      position="relative"
      borderRadius="xl"
      borderWidth="2px"
      borderColor={borderColor}
      borderStyle="dashed"
      w="full"
      bg={bg}
      transition="all 0.2s"
      _hover={{
        borderColor: hoverBorder,
        borderStyle: "solid",
      }}
    >
      <Flex
        position="absolute"
        top="-12px"
        left={4}
        right={4}
        justify="space-between"
        align="center"
      >
        <Text
          fontSize="xs"
          color="indigo.600"
          bg={bg}
          px={2}
          fontWeight="semibold"
        >
          Signature
        </Text>
        <IconButton
          aria-label="Clear signature"
          icon={<RiDeleteBin5Line />}
          size="xs"
          variant="ghost"
          color="gray.400"
          bg={bg}
          _hover={{color: "red.500", bg: "red.50"}}
          onClick={(e) => {
            e.stopPropagation();
            sigRef.current.clear();
            saveSignature("");
          }}
        />
      </Flex>
      <Box borderRadius="xl" overflow="hidden" p={2} minH="180px" display="flex" alignItems="center" justifyContent="center">
        <SignatureCanvas
          penColor="#4c1d95"
          maxWidth={2.5}
          ref={sigRef}
          onEnd={() => saveSignature(sigRef.current.toDataURL("image/png"))}
          canvasProps={{
            height: 180,
            style: {
              width: "100%",
              cursor: "crosshair",
            },
          }}
        />
      </Box>
    </Box>
  );
}

function PDFModal({form, setConsentForms, showModal}) {
  const bg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const confirmedBg = useColorModeValue("green.50", "green.900");
  const headingColor = useColorModeValue("gray.700", "gray.200");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const maxW = useBreakpointValue({base: "95vw", md: "60rem", lg: "70rem"});
  const maxH = useBreakpointValue({base: "95vh", md: "90vh"});
  const hasBuiltSections = Array.isArray(form?.sections) && form.sections.length > 0;

  const renderConsentElement = (node, keyPath) => {
    if (node == null) return null;
    if (typeof node !== "object") return node;

    const {element, key, props = {}, children} = node;
    const reactKey = `${keyPath}-${key || element}`;
    const sanitizedProps = {...props, key: reactKey};
    delete sanitizedProps.onChange;
    delete sanitizedProps.onClick;

    if (key === "SignatureElement" || element === "canvas") {
      return (
        <Box
          key={reactKey}
          minH="110px"
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg="white"
        />
      );
    }

    if (element === "input") {
      sanitizedProps.disabled = true;
      sanitizedProps.readOnly = true;
      if (sanitizedProps.type === "checkbox" || sanitizedProps.type === "radio") {
        sanitizedProps.checked = !!sanitizedProps.checked;
      } else if (sanitizedProps.value === undefined && sanitizedProps.defaultValue === undefined) {
        sanitizedProps.defaultValue = "";
      }
    }

    if (element === "textarea" || element === "select") {
      sanitizedProps.disabled = true;
      sanitizedProps.readOnly = true;
      if (sanitizedProps.value === undefined && sanitizedProps.defaultValue === undefined) {
        sanitizedProps.defaultValue = "";
      }
    }

    const renderedChildren = Array.isArray(children)
      ? children.map((child, index) => renderConsentElement(child, `${reactKey}-${index}`))
      : children;

    return React.createElement(element, sanitizedProps, renderedChildren);
  };

  const handleToggleConfirm = () => {
    setConsentForms((prev) => {
      return prev.map((e) => {
        return e.fid === form.fid
          ? {...e, confirmed: !e.confirmed}
          : e;
      });
    });
    showModal((prev) => ({...prev, confirmed: !prev.confirmed}));
  };

  if (!form) return null;

  return (
    <Modal
      isOpen={!!form}
      onClose={() => showModal(null)}
      size="full"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        maxW={maxW}
        maxH={maxH}
        h={maxH}
        bg={bg}
        borderRadius="xl"
        display="flex"
        flexDirection="column"
        boxShadow="2xl"
      >
        <Flex
          p={4}
          borderBottomWidth="1px"
          borderBottomColor={borderColor}
          align="center"
          justify="space-between"
        >
          <VStack align="start" spacing={0}>
            <Heading size="sm" color={headingColor}>
              {form?.formTitle?.replace(/(\.\w+)$/, "") || "Consent Form"}
            </Heading>
            <Text fontSize="xs" color={subTextColor}>
              Please read the document carefully
            </Text>
          </VStack>
          <ModalCloseButton position="relative" top={0} right={0} />
        </Flex>
        
        <ModalBody
          flex="1"
          display="flex"
          flexDirection="column"
          p={0}
          overflow="hidden"
        >
          <Box
            flex="1"
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
            overflow="hidden"
            m={4}
            mb={2}
            bg="gray.50"
          >
            {form?.data ? (
              <Box
                as="iframe"
                src={form?.data}
                w="100%"
                h="100%"
                title={`Consent form: ${form?.formTitle || "Document"}`}
                border="none"
              />
            ) : hasBuiltSections ? (
              <Box h="100%" overflowY="auto" p={4}>
                <VStack spacing={4} align="stretch">
                  {form.sections.map((section, sectionIdx) => (
                    <Box
                      key={`section-${sectionIdx}`}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="lg"
                      bg={bg}
                      overflow="hidden"
                    >
                      {section.name && (
                        <Box
                          px={4}
                          py={3}
                          bg="gray.50"
                          borderBottomWidth="1px"
                          borderBottomColor={borderColor}
                        >
                          <Text fontWeight="600" color={headingColor}>
                            {section.name}
                          </Text>
                        </Box>
                      )}
                      <Box px={4} py={4}>
                        <VStack spacing={4} align="stretch">
                          {(section.fields || []).map((row, rowIdx) => (
                            <Flex
                              key={`section-${sectionIdx}-row-${rowIdx}`}
                              gap={4}
                              wrap="wrap"
                              align="stretch"
                            >
                              {(row || []).filter(Boolean).map((field, colIdx) => (
                                <Box
                                  key={`section-${sectionIdx}-row-${rowIdx}-col-${colIdx}`}
                                  flex={row.length > 1 ? "1 1 0%" : "1 1 100%"}
                                  minW={row.length > 1 ? "260px" : "100%"}
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                  borderRadius="md"
                                  p={4}
                                  bg="white"
                                >
                                  {renderConsentElement(field, `section-${sectionIdx}-row-${rowIdx}-col-${colIdx}`)}
                                </Box>
                              ))}
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </Box>
            ) : (
              <Center h="100%">
                <Text fontSize="sm" color={subTextColor}>
                  This consent form could not be previewed.
                </Text>
              </Center>
            )}
          </Box>
          
          <Box
            p={4}
            bg={form?.confirmed ? confirmedBg : "transparent"}
            borderTopWidth="1px"
            borderTopColor={borderColor}
          >
            <Button
              w="full"
              size="lg"
              colorScheme={form?.confirmed ? "green" : "indigo"}
              variant={form?.confirmed ? "solid" : "outline"}
              leftIcon={<Icon as={form?.confirmed ? HiShieldCheck : FiCircle} />}
              rightIcon={form?.confirmed ? <Icon as={FiCheckCircle} /> : null}
              onClick={handleToggleConfirm}
              _hover={{
                transform: "translateY(-1px)",
                boxShadow: "md",
              }}
              transition="all 0.2s"
            >
              {form?.confirmed
                ? "Terms Accepted"
                : "I accept the terms mentioned in this form"}
            </Button>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
